import { useEffect } from "react";
import { Head, router, usePoll } from "@inertiajs/react";
import TvSlideshow from "@/components/tv/TvSlideshow";
import TvStage from "@/components/tv/TvStage";

// If the page can't be reached when it's time for a full reload, try
// again after this long rather than reloading onto an error page.
const RELOAD_RETRY_MS = 60_000;

/**
 * The school's TV display (BUSINESS_RULES §9): a 1080p slideshow of the
 * read-a-thon that rotates through whichever of the goal, school
 * rewards, top readers and each class's top reader the school has
 * switched on, re-fetching its numbers (and those choices) every minute.
 */
export default function TvDisplayShow({ poll_seconds, reload_seconds, ...content }) {
  useKeepShowing(poll_seconds, reload_seconds);

  return (
    <>
      <Head title={`${content.school.name} read-a-thon`} />
      <TvStage>
        <TvSlideshow content={content} />
      </TvStage>
    </>
  );
}

// Keeps a screen nobody touches for weeks running:
// - Re-fetches the props on a timer. A failed refresh (the network drops,
//   a deploy is mid-restart, a 429) keeps the last numbers on screen
//   instead of Inertia's error modal; the next poll tries again. A code
//   that's been replaced comes back as the code screen, which Inertia
//   shows as usual.
// - Reloads the whole page every few hours, but only once the page loads
//   cleanly: a reload during a Wi-Fi drop would otherwise leave the TV on
//   the browser's own error page, which never comes back by itself.
function useKeepShowing(pollSeconds, reloadSeconds) {
  usePoll(pollSeconds * 1000, { headers: { "X-TV-Poll": "1" } });

  useEffect(() => {
    const offInvalid = router.on("invalid", (event) => event.preventDefault());
    const offException = router.on("exception", (event) => event.preventDefault());

    let timer;
    const reloadWhenReachable = async () => {
      try {
        const response = await fetch(window.location.href, { cache: "no-store", headers: { "X-TV-Poll": "1" } });
        if (response.ok) return window.location.reload();
      } catch {
        // Offline: try again shortly.
      }
      timer = setTimeout(reloadWhenReachable, RELOAD_RETRY_MS);
    };
    timer = setTimeout(reloadWhenReachable, reloadSeconds * 1000);

    return () => {
      offInvalid();
      offException();
      clearTimeout(timer);
    };
  }, [reloadSeconds]);
}
